<template>
  <div class="main-request-list">
    <div class="page-header">
      <h2>메인오더 관리</h2>
      <div class="header-actions">
        <el-button
          v-if="userStore.isMainRequestManager"
          type="primary"
          @click="openCreateDialog"
        >
          <el-icon><Plus /></el-icon>
          신규 요청
        </el-button>
        <el-button
          :disabled="!selectedRows.length"
          @click="downloadSourcingForm"
        >
          소싱 폼 다운로드{{ selectedRows.length ? ` (${selectedRows.length})` : '' }}
        </el-button>
        <el-button @click="showHelp">
          <el-icon><QuestionFilled /></el-icon>
          도움말
        </el-button>
      </div>
    </div>

    <!-- 필터 영역 -->
    <el-card class="filter-card">
      <el-form :inline="true" :model="filters">
        <el-form-item label="법인">
          <el-select v-model="filters.company" clearable placeholder="전체">
            <el-option v-for="c in codeStore.ourCompanys" :key="c.id" :label="c.name" :value="c.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="브랜드">
          <el-select v-model="filters.brand" clearable placeholder="전체">
            <el-option
              v-for="brand in brands"
              :key="brand.id"
              :label="brand.name"
              :value="brand.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="상태">
          <el-select v-model="filters.status" clearable placeholder="전체">
            <el-option label="요청접수" value="REQUESTED" />
            <el-option label="자재발주중" value="MATERIAL_ORDERING" />
            <el-option label="생산중" value="IN_PRODUCTION" />
            <el-option label="선적중" value="SHIPPING" />
            <el-option label="납품완료" value="DELIVERED" />
            <el-option label="정산완료" value="COMPLETED" />
            <el-option label="취소" value="CANCELLED" />
          </el-select>
        </el-form-item>
        <el-form-item label="납기일">
          <el-date-picker
            v-model="filters.deliveryRange"
            type="daterange"
            range-separator="~"
            start-placeholder="시작일"
            end-placeholder="종료일"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="fetchData">검색</el-button>
          <el-button @click="resetFilters">초기화</el-button>
          <el-button :type="showAll ? '' : 'warning'" @click="toggleShowAll" size="small">
            {{ showAll ? '스마트뷰' : '전체보기' }}
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 데이터 테이블 -->
    <el-card class="table-card">
      <el-table
        :data="mainRequests || []"
        v-loading="loading"
        @row-click="handleRowClick"
        @selection-change="handleSelectionChange"
        stripe
        highlight-current-row
      >
        <el-table-column type="selection" width="42" />
        <el-table-column prop="request_number" label="요청번호" width="140" />
        <el-table-column prop="company_name" label="법인" width="100" />
        <el-table-column prop="brand_name" label="브랜드" width="120" />
        <el-table-column prop="style_number" label="스타일" width="120" />
        <el-table-column label="상태" width="100">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.status)">
              {{ row.status_display }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="order_quantity" label="발주수량" width="100" align="right">
          <template #default="{ row }">
            {{ formatNumber(row.order_quantity) }}
          </template>
        </el-table-column>
        <el-table-column prop="production_quantity" label="생산수량" width="100" align="right">
          <template #default="{ row }">
            <span>{{ formatNumber(row.production_quantity) }}</span>
            <span v-if="row.allowance_rate > 0" style="color:#909399; font-size:11px; margin-left:2px">
              (+{{ row.allowance_rate }}%)
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="delivery_date" label="납기일" width="120" />
        <el-table-column label="D-Day" width="80" align="center">
          <template #default="{ row }">
            <el-tag
              :type="row.days_until_delivery < 0 ? 'danger' : row.days_until_delivery <= 7 ? 'warning' : 'info'"
              size="small"
            >
              {{ row.days_until_delivery < 0 ? 'D+' + Math.abs(row.days_until_delivery) : 'D-' + row.days_until_delivery }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column v-if="userStore.hasCostAccess" label="단가" width="100" align="right">
          <template #default="{ row }">
            {{ formatCurrency(row.unit_cost) }}
          </template>
        </el-table-column>
        <!-- 원가보고 상태 (2026-07-29) — 담당자용 A안: 내 오더가 보고됐는지 한눈에 -->
        <el-table-column v-if="userStore.hasCostAccess" label="원가보고" width="90" align="center">
          <template #default="{ row }">
            <el-tag size="small" effect="plain" :type="costReportTagType(row.cost_report_status)">
              {{ costReportLabel(row.cost_report_status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="manufacturer_name" label="제조공장" width="120" />
        <el-table-column prop="created_at" label="등록일" width="120">
          <template #default="{ row }">
            {{ formatDate(row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column label="입력" width="70" align="center">
          <template #default="{ row }">
            <el-tag v-if="row.is_auto" size="small" effect="dark" color="#1f3a5f" style="border:none"
                    :title="`자동 변환 — 원본: ${row.auto_origin?.source_file || ''}${row.auto_origin?.sheet_name ? ' / ' + row.auto_origin.sheet_name : ''}`">자동</el-tag>
            <span v-else style="color:#909399;font-size:11px">직접</span>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.pageSize"
        :page-sizes="[20, 50, 100]"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next"
        @size-change="fetchData"
        @current-change="fetchData"
      />
    </el-card>

    <!-- 생성 다이얼로그 -->
    <MainRequestFormDialog
      v-model="showCreateDialog"
      @saved="handleSaved"
    />

    <!-- 도움말 패널 -->
    <HelpPanel
      v-model="helpVisible"
      :content-path="'screens/main-request-list'"
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { Plus, QuestionFilled } from '@element-plus/icons-vue'
import axiosInstance from '@/api/axios'
import { useCodeStore } from '@/stores/codeStore'
import MainRequestFormDialog from '@/components/production/MainRequestFormDialog.vue'
import HelpPanel from '@/components/help/HelpPanel.vue'

const router = useRouter()
const userStore = useUserStore()
const codeStore = useCodeStore()

const loading = ref(false)
const mainRequests = ref([])
const brands = ref([])
const helpVisible = ref(false)
const showCreateDialog = ref(false)
const showAll = ref(false)
const selectedRows = ref([])

const filters = reactive({
  company: null,
  brand: null,
  status: null,
  deliveryRange: null
})

const pagination = reactive({
  page: 1,
  pageSize: 20,
  total: 0
})

const getStatusType = (status) => {
  const types = {
    'REQUESTED': 'info',
    'MATERIAL_ORDERING': 'warning',
    'IN_PRODUCTION': '',
    'SHIPPING': 'success',
    'DELIVERED': 'success',
    'COMPLETED': 'info',
    'CANCELLED': 'danger'
  }
  return types[status] || 'info'
}

const formatNumber = (num) => {
  return num?.toLocaleString() || '0'
}

const formatCurrency = (amount) => {
  return amount ? `$${Number(amount).toFixed(2)}` : '-'
}

const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  return dateStr.split('T')[0]
}

// 원가보고 상태 (2026-07-29)
const costReportLabel = (s) => ({
  NONE: '미보고', PENDING: '결재중', APPROVED: '승인', REJECTED: '반려'
}[s] || '미보고')

const costReportTagType = (s) => ({
  NONE: 'info', PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger'
}[s] || 'info')

const fetchData = async () => {
  loading.value = true
  try {
    const params = {
      page: pagination.page,
      page_size: pagination.pageSize,
    }
    if (filters.company) params.company = filters.company
    if (filters.brand) params.brand = filters.brand
    if (filters.status) params.status = filters.status
    if (filters.deliveryRange && filters.deliveryRange.length === 2) {
      params.delivery_from = filters.deliveryRange[0]
      params.delivery_to = filters.deliveryRange[1]
    }

    // Smart filter: exclude completed and cancelled items when showAll is false
    if (!showAll.value && !filters.status) {
      params.status__in = ['REQUESTED', 'MATERIAL_ORDERING', 'IN_PRODUCTION', 'SHIPPING', 'DELIVERED']
    }

    const response = await axiosInstance.get('production/main-requests/', { params })
    mainRequests.value = response.data.results || response.data
    pagination.total = response.data.count || mainRequests.value.length
  } catch (error) {
    console.error('Failed to fetch main requests:', error)
  } finally {
    loading.value = false
  }
}

const fetchBrands = async () => {
  try {
    const response = await axiosInstance.get('brands/')
    brands.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch brands:', error)
  }
}

const resetFilters = () => {
  filters.company = null
  filters.brand = null
  filters.status = null
  filters.deliveryRange = null
  pagination.page = 1
  showAll.value = false
  fetchData()
}

const toggleShowAll = () => {
  showAll.value = !showAll.value
  pagination.page = 1
  fetchData()
}

const handleRowClick = (row) => {
  router.push(`/main-requests/${row.id}`)
}

const handleSelectionChange = (rows) => {
  selectedRows.value = rows
}

// 소싱 폼 다운로드 (C-2, 2026-07-17) — 선택 작지를 지사 발송 카드 서식으로
const downloadSourcingForm = async () => {
  if (!selectedRows.value.length) return
  try {
    const ids = selectedRows.value.map((r) => r.id).join(',')
    const res = await axiosInstance.get('production/main-requests/sourcing-form/', {
      params: { ids },
      responseType: 'blob',
    })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `소싱폼_${new Date().toISOString().slice(0, 10)}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    console.error('sourcing form failed:', e)
  }
}

const openCreateDialog = () => {
  showCreateDialog.value = true
}

const handleSaved = () => {
  showCreateDialog.value = false
  fetchData()
}

const showHelp = () => {
  helpVisible.value = true
}

onMounted(() => {
  fetchData()
  fetchBrands()
  codeStore.fetchOurCompanys()
})
</script>

<style scoped>
.main-request-list {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.filter-card {
  margin-bottom: 20px;
}

.table-card {
  margin-bottom: 20px;
}

.el-table {
  cursor: pointer;
}

.el-pagination {
  margin-top: 20px;
  justify-content: flex-end;
}
</style>
